import { SUPORTE_TTL_SEGUNDOS } from '@vestigo/core/area-impersonacao';
import { useT } from '../lib/idioma.js';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { lerFaixaDeSuporte } from '../lib/suporte.js';

/**
 * A faixa da sessão de suporte.
 *
 * ## O que ela impede
 *
 * Que alguém feche a aba achando que era a própria conta. Um operador que entrou
 * como um aluno está olhando **a conta de outra pessoa**, com o e-mail dela na
 * marca d'água do vídeo e o progresso dela nas barras — e sem nada dizendo isso,
 * a única pista seria o nome no cabeçalho, que ele não tem motivo para ler.
 *
 * ## Por que ela é um objeto ESTRANHO à página
 *
 * A área do aluno é pintada pelo cliente: `--fundo`, `--superficie`, `--texto` e
 * `--destaque` saem do Estúdio, e podem ser qualquer coisa — preto e verde-limão,
 * bege e marrom. Uma faixa desenhada com essas variáveis se dissolveria em
 * metade das áreas, e justamente nas mais bem acabadas.
 *
 * Então ela não usa nenhuma delas. As cores daqui são fixas e propositalmente de
 * outro mundo: betume e âmbar de sódio, a paleta de equipamento — a mesma
 * gramática de uma barra de "você está compartilhando a tela". Ela não pertence
 * ao curso, e é isso que ela precisa comunicar antes de qualquer palavra.
 *
 * ## A assinatura: o fio que escoa
 *
 * O fio âmbar na borda de cima **é o prazo**. Ele começa varrendo a largura da
 * tela e encurta até sumir, e quando some a sessão acabou. O número em mono está
 * lá para quem quer o número; o fio é para quem não está olhando — dá para
 * perceber pelo canto do olho que resta pouco, sem um relógio piscando sobre a
 * aula que se está avaliando.
 *
 * É a única coisa que se move nesta tela, e é o motivo de a faixa poder ser
 * silenciosa no resto.
 */

/* Betume, âmbar de sódio, papel. Nenhuma variável do tema do cliente entra aqui
 * — ver o cabeçalho. `#ff6b3d` só aparece no último minuto. */
const CORPO = '#17110a';
const AMBAR = '#ffb02e';
const PAPEL = '#f4ead8';
const FRACO = '#a3937a';
const URGENTE = '#ff6b3d';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

function relogio(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function FaixaDeSuporte() {
  const t = useT();
  /* A leitura do cookie mora em `lib/suporte.ts` porque ela tem um segundo
     chamador: o mural, que desabilita o que não pode ser escrito nesta sessão em
     vez de deixar digitar e recusar no fim. Ver o cabeçalho de lá. */
  const [faixa] = useState(lerFaixaDeSuporte);
  const [agora, setAgora] = useState(() => Date.now());

  /*
   * Um `setInterval` de um segundo, e só quando há faixa.
   *
   * O aluno de verdade nunca chega a montar este efeito — `faixa` é nula, o
   * componente devolve `null` antes, e o único custo que ele paga por este
   * recurso existir é a leitura de um cookie no carregamento.
   */
  useEffect(() => {
    if (!faixa) return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [faixa]);

  /*
   * A altura da faixa é devolvida ao documento.
   *
   * Sem isto ela cobre a última linha da lista de aulas e o botão de concluir —
   * e o operador conclui que a tela está cortada, que é exatamente o tipo de
   * defeito que ele veio investigar.
   */
  useEffect(() => {
    if (!faixa) return;
    const anterior = document.body.style.paddingBottom;
    document.body.style.paddingBottom = '4.5rem';
    return () => {
      document.body.style.paddingBottom = anterior;
    };
  }, [faixa]);

  if (!faixa) return null;

  const fim = new Date(faixa.expiraEm).getTime();
  /* O prazo inteiro vem do core — o mesmo número que decidiu o TTL da sessão no
     Redis. Escrevê-lo aqui faria a barra escoar num ritmo que não é o da sessão
     no dia em que o prazo mudasse, e ninguém repararia. */
  const totalMs = SUPORTE_TTL_SEGUNDOS * 1000;
  const restanteMs = Math.max(0, fim - agora);
  const restante = Math.ceil(restanteMs / 1000);
  const acabou = restanteMs <= 0;
  const ultimoMinuto = restante <= 60 && !acabou;

  async function sair() {
    /* A ordem importa: derrubar a sessão primeiro, navegar depois. O caminho
       inverso deixaria uma sessão de aluno viva no navegador do operador. */
    await api.post('/sair').catch(() => undefined);
    window.location.href = faixa!.voltarPara || '/';
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50"
      style={{ background: CORPO, color: PAPEL, fontFamily: 'inherit' }}
    >
      {/* O fio que escoa. `aria-hidden` porque o número ao lado já diz o mesmo,
          e um leitor de tela não tem o que fazer com uma largura. */}
      <div aria-hidden style={{ height: 2, background: 'rgba(255,255,255,0.06)' }}>
        <div
          style={{
            height: '100%',
            width: `${(restanteMs / totalMs) * 100}%`,
            background: ultimoMinuto ? URGENTE : AMBAR,
            /* Um segundo, linear: a barra anda no mesmo passo do relógio em vez
               de pular. Desligado por `prefers-reduced-motion` no CSS abaixo. */
            transition: 'width 1s linear',
          }}
          className="vst-suporte-fio"
        />
      </div>

      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
        <span
          title={`Sessão iniciada por ${faixa.operador}`}
          style={{
            fontFamily: MONO,
            fontSize: 9.5,
            letterSpacing: '0.16em',
            color: acabou ? FRACO : AMBAR,
            textTransform: 'uppercase',
          }}
        >
          {acabou ? t('suporte.sessaoEncerrada') : t('suporte.sessaoDeSuporte')}
        </span>

        <p className="min-w-0 flex-1 text-[13px] leading-snug">
          {acabou ? (
            <>{t('suporte.prazoTerminou')}</>
          ) : (
            <>
              {t('suporte.vendoComo')} <strong className="font-semibold">{faixa.aluno}</strong>
              <span style={{ color: FRACO }}> · {faixa.email}</span>
              <span style={{ color: FRACO }}> {t('suporte.nadaEGravadoNaConta')}</span>
            </>
          )}
        </p>

        {!acabou && (
          <span
            aria-label={`Faltam ${relogio(restante)} para a sessão terminar`}
            style={{
              fontFamily: MONO,
              fontSize: 13,
              /* Tabular: sem isto o número dança a cada segundo e puxa o botão
                 junto, que é a coisa mais irritante que uma barra fixa faz. */
              fontVariantNumeric: 'tabular-nums',
              color: ultimoMinuto ? URGENTE : PAPEL,
            }}
          >
            {relogio(restante)}
          </span>
        )}

        <button
          type="button"
          onClick={() => void sair()}
          className="shrink-0 px-3 py-1.5 text-[12.5px] font-medium"
          style={{
            background: acabou ? AMBAR : 'transparent',
            color: acabou ? CORPO : PAPEL,
            border: `1px solid ${acabou ? AMBAR : 'rgba(244,234,216,0.35)'}`,
            borderRadius: 3,
          }}
        >
          {faixa.voltarPara ? t('suporte.sairEVoltarAoPainel') : t('suporte.sairDaSimulacao')}
        </button>
      </div>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .vst-suporte-fio { transition: none !important; }
        }
      `}</style>
    </div>
  );
}
